/**
 * React Query hooks for camera CRUD + the /test probe.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  Camera,
  CameraCreateInput,
  CameraTestResult,
} from "@/types/cameras";


export function useCameras() {
  return useQuery<Camera[]>({
    queryKey: ["cameras"],
    queryFn: () => api<Camera[]>("/api/cameras"),
    refetchInterval: 10_000, // keep running/last_frame_age_sec fresh
    refetchIntervalInBackground: false,
  });
}

export function useCamera(cameraId: string | null | undefined) {
  return useQuery<Camera>({
    queryKey: ["camera", cameraId],
    queryFn: () => api<Camera>(`/api/cameras/${cameraId}`),
    enabled: Boolean(cameraId),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
}

export function useCreateCamera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CameraCreateInput) =>
      api<Camera>("/api/cameras", { method: "POST", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cameras"] });
    },
  });
}

export function useDeleteCamera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/cameras/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cameras"] });
    },
  });
}

export function useTestCamera() {
  return useMutation({
    mutationFn: (rtsp_url: string) =>
      api<CameraTestResult>("/api/cameras/test", {
        method: "POST",
        body: { rtsp_url },
      }),
  });
}

/**
 * C1-4 — edit an existing camera (name / RTSP URL / location / snapshot
 * mode). PATCH /api/cameras/{id} re-runs the SSRF guard and restarts only
 * that camera's stream, so a SIM-swap URL change no longer needs SQL + a
 * full server restart.
 */
export interface CameraPatch {
  name?: string;
  name_he?: string | null;
  rtsp_url?: string;
  location?: string | null;
  snapshot_mode?: string;
  is_active?: boolean;
}

export function usePatchCamera() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CameraPatch }) =>
      api<Camera>(`/api/cameras/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cameras"] }),
  });
}
